import { useCallback, useState, useMemo } from 'react';
import { AuthEvent, useIcaps } from '../icaps/useIcaps';
import useAuth0, { Auth0Event } from '../useAuth0';
import { authStateTransition, UnifiedAuthState } from './authStateTransitions';
import { useCartMerge } from '../useCartMerge';
import { useMenu } from '../useMenu';
import { ProfilePagesUrls } from '../../utils/Urls';
import { Actions } from '../../actions/enums';

const LoggedInStatuses: UnifiedAuthState['status'][] = ['MenuLoading', 'MenuError', 'LogoutError', 'Menu', 'Loyalty'];

export const useUnifiedAuth = (status: 'ciam' | 'icaps') => {
  const [authState, setAuthState] = useState<UnifiedAuthState>({
    status: 'Closed',
  });

  const isUsingCiam = status === 'ciam';
  const isUsingIcaps = status === 'icaps';
  const menu = useMenu(LoggedInStatuses.includes(authState.status), setAuthState);

  // Unified auth event handler for both CIAM and ICAPS
  const onAuthEvent = useCallback(
    (ev: AuthEvent | Auth0Event) =>
      setAuthState((prev) =>
        authStateTransition(ev, prev, {
          clearMenuCache: menu.clearCache,
          goToCheckout: () => globalThis.ikea.pubsub.publish(Actions.GO_TO_CHECKOUT),
          goToMissingInfo: () => {
            globalThis.location.href = ProfilePagesUrls.missingInfo();
          },
        }),
      ),
    [menu.clearCache],
  );

  const auth0Hook = useAuth0(isUsingCiam && authState.status !== 'Closed', onAuthEvent);
  const icapsHook = useIcaps(isUsingIcaps && authState.status !== 'Closed', onAuthEvent);
  const { loggedIn, logout } = isUsingIcaps ? icapsHook : auth0Hook;
  const icapsLogin = icapsHook.login;
  useCartMerge(authState, setAuthState);

  const setAuthStateExternal = useCallback(
    async (newState: 'Open' | 'Reminder' | 'Closed' | 'Loyalty') => {
      setAuthState(() => {
        if (newState === 'Closed') return { status: 'Closed' };

        if (!loggedIn) {
          return newState === 'Reminder'
            ? { status: 'Reminder', isLoggingIn: false }
            : { status: 'Unauthenticated', isLoggingIn: false };
        }

        if (menu.cached) {
          if (newState === 'Loyalty' && menu.cached.loyaltyCard) {
            return { status: 'Loyalty', data: menu.cached.loyaltyCard };
          }
          return { status: 'Menu', data: menu.cached };
        }

        return { status: 'MenuLoading' };
      });
    },
    [loggedIn, menu.cached],
  );

  return useMemo(
    () => ({
      authState,
      setAuthState: setAuthStateExternal,
      retryMenu: menu.retry,
      icapsLogin,
      logout,
    }),
    [authState, setAuthStateExternal, menu.retry, icapsLogin, logout],
  );
};
